/**
 * rxMemory — THE single derivation every Rx field-memory surface consumes
 * (group headings, filtering, ranking, template-wins dedupe, and the
 * optimistic post-save merge). Mirrors the `fieldTemplateRows()` /
 * `buildFieldGroups` shape in `rxTemplates.js` (read that first).
 *
 * Pure module — no React, no API, no store, no fetch. Every value here is
 * derived from the `memory` object the G1 `/reference` payload carries
 * (`build-plan.md` §2) or from a `memory_delta` returned by G2's save.
 *
 * Owner ruling (build-plan §1): the dropdown IS the full list — no row cap,
 * no `See all`, no second list. FE-3 owns the scroll container + count line
 * rendering; this module only computes the numbers and the ranked rows.
 */

// ── Types — mirrors G1's `memory.types` keys exactly ────────────────────
export const MEMORY_TYPES = {
  CO: 'CO',
  DX: 'DX',
  SYMPTOM: 'SYMPTOM',
  INV: 'INV',
  SURG: 'SURG',
  INSTR: 'INSTR',
  RX: 'RX',
};

/**
 * Copy — verbatim from the PO Copy list (`artifacts/stories.md` § Copy
 * list). Free-hand nothing else. `MEMORY_GROUP_OTHER_DOCTOR` carries a
 * `{doctorName}` placeholder; `MEMORY_COUNT_LINE` a `{n}` placeholder;
 * `MEMORY_COUNT_LINE_FILTERED` `{n}`/`{m}` — the caller interpolates.
 *
 * NOTE: the PO Copy list has no `MEMORY_GROUP_*` heading for the `INSTR`
 * wire type (S2 AC2 names six fields, not seven) — `INSTR` rows are a valid
 * `memoryFieldRows()` input (the contract carries them) but no surface
 * renders a group for them yet. Not this task's call to invent one.
 */
export const MEMORY_GROUP_COPY = {
  MEMORY_GROUP_CO: 'Your complaints',
  MEMORY_GROUP_SYMPTOM: 'Your symptoms',
  MEMORY_GROUP_DX: 'Your diagnoses',
  MEMORY_GROUP_RX: 'Your medicines',
  MEMORY_GROUP_TEST: 'Your tests',
  MEMORY_GROUP_SURG: 'Your surgeries',
  MEMORY_GROUP_OTHER_DOCTOR: 'Saved by {doctorName}',
  MEMORY_COUNT_LINE: '{n} saved',
  // PO-RATIFY-PENDING — not yet in the ratified Copy list (build-plan §7 item 1).
  MEMORY_COUNT_LINE_FILTERED: '{n} saved · {m} match',
};

/**
 * FE-4 — the lab-OFF Tests strip's own free-text input placeholder. PO Copy
 * list (`artifacts/stories.md` § Copy list), verbatim — not free-handed.
 */
export const TEST_TYPE_PLACEHOLDER = 'Type a test name and press Enter';

/**
 * FE-5 — Tests field typing, lab module ON (`stories.md` § Copy list,
 * verbatim). `TEST_OUTSIDE_HINT` replaces `LabListPicker`'s old "type the
 * test name instead" dead end now that its search box actually accepts
 * Enter.
 *
 * FIX-3 (owner ruling, live click-through) — the `TEST_OUTSIDE_BADGE`
 * ("Outside") text pill this constant used to back is REMOVED from every
 * Tests chip: the chip's existing amber/teal tone already carries the
 * in-house-vs-outside signal, and the badge was redundant noise on top of
 * it. This reverses the ratified Copy-list entry for `TEST_OUTSIDE_BADGE`
 * (`stories.md` § Copy list) — flagged for the PO to retire that row, not
 * only the code.
 */
export const TEST_OUTSIDE_HINT = 'Not in your catalogue — it will print on the prescription only.';

/**
 * FE-6 — the Advised surgeries pad section (`stories.md` § Copy list,
 * verbatim). `SURGERY_SECTION_LABEL` is the section's own label in the pad's
 * fixed gutter AND the word the copy-previous-Rx toast names, so the two can
 * never drift.
 */
export const SURGERY_SECTION_LABEL = 'Advised surgeries';
export const SURGERY_PLACEHOLDER = 'Type a surgery and press Enter';
export const SURGERY_SECTION_TOOLTIP = 'Surgeries you have advised. They print on the prescription and are saved for next time.';

/**
 * FE-8 — the SURGERIES template category's disabled `Save…` hint (`stories.md`
 * § Copy list, verbatim). Read by `templateSaveAffordance` (rxTemplates.js)
 * the SAME way every sibling category's hint is — one string, one place.
 */
export const SURGERY_SAVE_DISABLED_HINT = 'Add a surgery first';

/**
 * FE-12 — forget a remembered entry (`stories.md` § Copy list, verbatim).
 * `MEMORY_FORGET_ARIA`/`MEMORY_FORGET_BODY` carry a `{name}` placeholder,
 * interpolated by the caller (the row's own `v`) — never a second lookup.
 */
export const MEMORY_FORGET_ARIA = 'Remove "{name}" from your saved list';
export const MEMORY_FORGET_TITLE = 'Remove from your saved list?';
export const MEMORY_FORGET_BODY = '"{name}" will stop appearing in your suggestions. Prescriptions you have already saved are not changed.';
export const MEMORY_FORGET_KEEP = 'Keep';
export const MEMORY_FORGET_CONFIRM = 'Remove';
export const MEMORY_FORGET_TOAST = 'Removed from your saved list.';

/**
 * co-memory-backfill r5 — a derived (individual-value) C/O row has no store
 * row of its own to remove, so its "×" opens an INFORMATIONAL variant of the
 * same `ConfirmationModal` instead of the forget confirm: names the saved
 * line(s) it comes from, single dismiss, no remove action (`stories.md`
 * "Ratified copy — derived value ×", D1-D4, owner ruling 2026-09-15).
 * `CO_DERIVED_INFO_BODY` carries TWO placeholders — `{name}` (twice) and
 * `{lines}` (the caller's `row.from`, one quoted saved line per own line).
 */
export const CO_DERIVED_INFO_TITLE = "This isn't saved on its own";
export const CO_DERIVED_INFO_BODY = '"{name}" comes from what you saved: {lines}. Remove that line to stop seeing "{name}".';
export const CO_DERIVED_INFO_DISMISS = 'Got it';
export const CO_DERIVED_INFO_ARIA = 'See where "{name}" comes from';

/**
 * co-memory-backfill — the "this was whole line the doctor typed before"
 * marker (`stories.md` § Ratified copy, C1/C2, PO-ratified 2026-09-14 —
 * the owner's own word is "typed"). Chip only: NEVER concatenated into the
 * value a pick inserts into the C/O field (`DigitalPrescriptionForm.jsx`'s
 * `applyCcSuggestion` still inserts the row's plain `v`/`name`). An
 * individual-value row carries no chip at all — absence is the signal, so
 * there is deliberately no sibling "individual" copy constant here.
 */
export const CO_CHIP_LABEL = 'Typed';
export const CO_CHIP_TITLE = 'A full line you typed before';

/**
 * Wire type -> `MEMORY_GROUP_COPY` key, defined ONCE so every surface (FE-3's
 * dropdown groups today; any future memory UI) reads the SAME mapping —
 * never re-derived per component. `INSTR` has no PO-ratified group heading
 * (S2 AC2 names six fields, not seven): it is a valid `memoryFieldRows()`
 * input but deliberately absent here — see `memoryGroupHeading` below.
 */
export const MEMORY_TYPE_GROUP_KEY = {
  [MEMORY_TYPES.CO]: 'MEMORY_GROUP_CO',
  [MEMORY_TYPES.SYMPTOM]: 'MEMORY_GROUP_SYMPTOM',
  [MEMORY_TYPES.DX]: 'MEMORY_GROUP_DX',
  [MEMORY_TYPES.RX]: 'MEMORY_GROUP_RX',
  [MEMORY_TYPES.INV]: 'MEMORY_GROUP_TEST',
  [MEMORY_TYPES.SURG]: 'MEMORY_GROUP_SURG',
  // MEMORY_TYPES.INSTR — no key. `memoryGroupHeading` returns null for it;
  // a caller MUST treat null as "render no group" — never throw, never fall
  // back to an unnamed/raw-key heading.
};

/**
 * The group heading for a field's memory list, or `null` when the type has
 * no ratified heading (today, only `INSTR`). `owner` is the G1 response's
 * `memory.owner` object — `is_self` (from the RESPONSE, never a client
 * guess — Q9) picks `Your …` vs `Saved by {doctorName}`.
 *
 * bk-1023 — `owner.name` is a best-effort server lookup and can be `""`
 * (missing/nameless member row). A blank name must never produce a
 * dangling "Saved by " heading: fall back to the plain self heading
 * instead of interpolating an empty string. No new copy key — the PO Copy
 * list has no ratified generic "Saved by another doctor" string yet.
 */
export function memoryGroupHeading(type, owner) {
  const copyKey = MEMORY_TYPE_GROUP_KEY[type];
  if (!copyKey) return null;
  const doctorName = owner && owner.is_self === false ? String(owner.name || '').trim() : '';
  if (doctorName) {
    return MEMORY_GROUP_COPY.MEMORY_GROUP_OTHER_DOCTOR.replace('{doctorName}', doctorName);
  }
  return MEMORY_GROUP_COPY[copyKey];
}

/**
 * The count line above the scrollable memory list — `MEMORY_COUNT_LINE`
 * ("{n} saved") when the field's box is empty, `MEMORY_COUNT_LINE_FILTERED`
 * ("{n} saved · {m} match" — PO-RATIFY-PENDING) while typing filters it.
 * `total`/`matched` are `memoryFieldRows()`'s own numbers — never re-derived
 * per surface (FE-3 Diagnosis today; FE-3B C/O and Medicines next).
 */
export function memoryCountLine(total, matched, query) {
  const filtering = String(query || '').trim().length > 0;
  const template = filtering ? MEMORY_GROUP_COPY.MEMORY_COUNT_LINE_FILTERED : MEMORY_GROUP_COPY.MEMORY_COUNT_LINE;
  return template.replace('{n}', total).replace('{m}', matched);
}

/** Trim → collapse whitespace → casefold. Mirrors BE-1's normalisation rule. */
export function normaliseMemoryValue(text) {
  return String(text == null ? '' : text)
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/**
 * Row-IDENTITY key — the single source for "is this the SAME suggestion"
 * anywhere in a dropdown (owner ruling: a doctor must never see the same
 * suggestion twice). `normaliseMemoryValue` (casefold + collapse whitespace)
 * plus stripping TRAILING punctuation, so "Acute Gastroenteritis" and
 * "acute   gastroenteritis." collide. Distinct from `normaliseMemoryValue`
 * itself, which stays BE-1's plain mirror for query substring matching.
 */
export function normaliseRowKey(text) {
  return normaliseMemoryValue(text).replace(/[.,;:!?]+$/, '');
}

/**
 * FE-14 — medicine-specific identity key. Same as `normaliseRowKey`, plus
 * hyphens folded to spaces and a trailing dose-unit token ("mg"/"mg tablet")
 * stripped, so "Dolo-650", "dolo 650" and "Dolo 650mg" collide to one row.
 * Only the medicines dropdown fold uses this — every other field keeps
 * `normaliseRowKey` unchanged (no unit token to strip on a symptom/diagnosis).
 */
export function normaliseMedicineRowKey(text) {
  const noHyphens = String(text == null ? '' : text).replace(/-/g, ' ');
  const noDoseUnit = noHyphens.replace(/\s*mg\s*tablet\s*$/i, '').replace(/\s*mg\s*$/i, '');
  return normaliseRowKey(noDoseUnit);
}

/** Ranked `c` (usage_count) desc, then `t` (last_used_at) desc. New array, input untouched. */
function rankRows(rows) {
  return [...rows].sort((a, b) => (b.c - a.c) || (b.t - a.t));
}

/**
 * (a) Legacy rows that resolve to the same `normaliseRowKey` fold into ONE —
 * usage counts SUMMED, the casing of the higher-count individual row wins
 * ("most-used casing displayed"), `t` is the more recent of the two. Any
 * extra fields (RX's `k`/`variants`) ride along on the winning row.
 */
function foldDuplicateRows(rows) {
  const byKey = new Map();
  for (const row of rows) {
    const key = normaliseRowKey(row.v);
    const existing = byKey.get(key);
    if (!existing) { byKey.set(key, { ...row }); continue; }
    const winner = row.c > existing.c ? row : existing;
    byKey.set(key, { ...winner, c: existing.c + row.c, t: Math.max(existing.t, row.t) });
  }
  return [...byKey.values()];
}

/**
 * (b) Memory vs a field's SYSTEM rows (static lists, parser aliases, the
 * medicine master, learned "★ Your pick" rows) — on a collision the MEMORY
 * row wins. Returns `systemRows` with every colliding entry REMOVED; the
 * caller renders its memory rows as-is alongside this filtered list, so the
 * same suggestion is never offered from both sources at once.
 * `getSystemText` extracts the comparable string from a system row (plain
 * strings by default; pass an extractor for richer rows, e.g. `{name}`).
 *
 * FE-14 — `opts.getSystemCode` (default: none) extracts a master-catalog
 * `code` from a system row; when BOTH sides carry a code the collision is
 * decided by code FIRST (a coded system row folds into its memory twin even
 * when the display names differ) — a codeless system row still falls back to
 * the text match below. `opts.normaliseKey` (default `normaliseRowKey`) lets
 * a caller swap in a field-specific identity key (e.g. `normaliseMedicineRowKey`)
 * without changing this function's fold order for every other field.
 */
export function dedupeSystemRows(systemRows, memoryRows, getSystemText = (row) => row, opts = {}) {
  const { getSystemCode = () => null, normaliseKey = normaliseRowKey } = opts;
  const memoryCodes = new Set((memoryRows || []).map((row) => row.code).filter(Boolean));
  const memoryKeys = new Set((memoryRows || []).map((row) => normaliseKey(row.v)));
  return (systemRows || []).filter((row) => {
    const code = getSystemCode(row);
    if (code && memoryCodes.has(code)) return false;
    return !memoryKeys.has(normaliseKey(getSystemText(row)));
  });
}

/**
 * Substring, ANY word, over `v` (the saved value) — never a prefix-only or
 * ≥2-char gate. "gastro" matches "Acute gastroenteritis"; "enteritis" does
 * too (today's field logic is `startswith` on the typed key — this is the
 * read-side fix).
 */
export function filterMemoryRows(rows, query) {
  const q = normaliseMemoryValue(query);
  if (!q) return rows;
  return rows.filter((row) => normaliseMemoryValue(row.v).includes(q));
}

/**
 * memoryFieldRows — THE per-field projection. The FULL ranked list (no cap,
 * no `See all` — owner ruling), with EVERY duplicate-suppression rule
 * applied so a doctor never sees the same suggestion twice in one dropdown:
 *   (a) legacy same-identity rows folded into one (`foldDuplicateRows`);
 *   (b) template-wins dedupe — a row equal to a TEMPLATE's own text is
 *       hidden (the template group itself is never deduped against memory —
 *       a template's NAME is not its content, owner ruling);
 *   (c) a row already present in what the doctor has typed is never offered.
 * `total` = rows surviving all of the above, before the query filter;
 * `matched` = rows after the query filter — the two numbers the count line
 * is built from (see `memoryCountLine`).
 *
 * @param {Object} memory        the G1 payload's `memory` object (or undefined)
 * @param {string} type          MEMORY_TYPES.*
 * @param {string} query         what the doctor has typed in that field
 * @param {{ templateRows?: string[], alreadySelectedText?: string }} opts
 *   `templateRows` — the field's template row texts (D25 group).
 *   `alreadySelectedText` — the field's current raw value; a memory row
 *   whose identity is already contained in it is dropped (c).
 * @returns {{ rows: Array, total: number, matched: number }}
 */
export function memoryFieldRows(memory, type, query = '', { templateRows = [], alreadySelectedText = '' } = {}) {
  const allRows = memory?.types?.[type]?.rows || [];
  const folded = foldDuplicateRows(allRows);
  const templateSet = new Set((templateRows || []).map(normaliseRowKey));
  const notTemplated = folded.filter((row) => !templateSet.has(normaliseRowKey(row.v)));
  const selectedKey = normaliseRowKey(alreadySelectedText);
  const notAlreadySelected = selectedKey
    ? notTemplated.filter((row) => !selectedKey.includes(normaliseRowKey(row.v)))
    : notTemplated;
  const ranked = rankRows(notAlreadySelected);
  const matchedRows = filterMemoryRows(ranked, query);
  return { rows: matchedRows, total: ranked.length, matched: matchedRows.length };
}

// ── co-memory-backfill — C/O individual values (architect-design.md §3) ──
// Read-side only: the STORE still holds whole lines (`memory.types.CO.rows`,
// unchanged), this section only derives what a C/O dropdown OFFERS from
// them. No new wire field, no new endpoint (`build-plan.md` § API CONTRACT).

/** Mirrors the planner's own clause-length guard (`_MAX_NORMALISED_LEN`) —
 * a clause this long is noise, not a pickable complaint. */
const CO_CLAUSE_MAX_LEN = 200;

/**
 * splitMemoryClauses — COMMA ONLY (architect-design.md §6, the owner's own
 * words: "when comma is used"). Deliberately narrower than the pad's own
 * `ACTIVE_SEGMENT_SPLIT_RE` (`DigitalPrescriptionForm.jsx`, which also
 * splits on `&`/`+`/"and" to find "which segment am I typing now") — the
 * two rules answer different questions and must never be unified. No
 * tail-stripping beyond `trim` — "fever since 3 days" stays ONE clause,
 * verbatim.
 */
export function splitMemoryClauses(line) {
  return String(line == null ? '' : line)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => normaliseRowKey(part).length > 0 && part.length <= CO_CLAUSE_MAX_LEN);
}

/**
 * expandCoRows — derives BOTH pickable shapes from the store's whole
 * lines, memoised by the `rows` array's own IDENTITY (a WeakMap keyed on
 * the `memory.types.CO.rows` reference) so a re-render / keystroke that
 * did not change `memory` never re-derives (architect-design.md §10.4,
 * explicit AC — this must NOT run per keystroke).
 *
 * OWNER RULING 2026-09-15 (`stories.md` "OWNER RULING 2026-09-15" —
 * supersedes architect-design.md §3.4's "single-clause line appears in
 * BOTH groups" and the owner example's own twin line): a folded store row
 * whose `v` carries NO comma is itself a single value — it is emitted
 * ONCE, `shape:'individual'`, `src:'store'` (so it stays forgettable via
 * the real row backing it). It never gets a `shape:'typed'` twin. A row
 * whose `v` DOES contain a comma is emitted as `shape:'typed'`,
 * `src:'store'` PLUS its comma-split clauses as `shape:'individual'`
 * candidates (as before).
 *
 * `shape:'individual'` rows are DEDUPED on `normaliseRowKey` **within the
 * individual group only**: `c` = SUM of every contributing row's `c`,
 * `t` = MAX, display casing = the clause text belonging to the
 * highest-`c` contributor. A comma-free store line contributes
 * `src:'store'`; a clause carved out of a longer (comma-bearing) line
 * contributes `src:'derived'` — no row of its own to forget. When a key
 * receives BOTH kinds, `src:'store'` wins so the combined value stays
 * forgettable via the real row backing it.
 *
 * r5 — a `src:'derived'` row additionally carries `from`: the comma-bearing
 * store line(s) (their `v`, verbatim) whose split clauses produced it, in
 * RANK order (`c` desc, `t` desc — the same order `rankRows` applies
 * everywhere else), so the informational "×" modal (`CO_DERIVED_INFO_BODY`)
 * never re-derives its own source list. A key that merges from more than one
 * comma line carries every one of them. `src:'store'` rows carry `from: []`
 * — they have a real store row of their own, the forget flow never reads it.
 *
 * bk-1287 — a line is comma-BEARING only when it splits into MORE THAN ONE
 * clause (`splitMemoryClauses(v).length > 1`), not merely "contains a comma
 * character". A trailing-comma-only line ("fever,") has exactly one clause
 * after the empty tail is dropped — it is a single value, same as a
 * comma-free line, not a typed-plus-derived pair.
 *
 * @param {Array<{v: string, c: number, t: number}>} rows  `memory.types.CO.rows`
 * @returns {Array<{v: string, c: number, t: number, shape: 'individual'|'typed', src: 'store'|'derived', from?: string[]}>}
 */
const _expandCoRowsCache = new WeakMap();
export function expandCoRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const cached = _expandCoRowsCache.get(rows);
  if (cached) return cached;

  // QA D-1 — `coMemoryRows` replaced `memoryFieldRows` on the C/O path but
  // dropped its `foldDuplicateRows` step, so two normalisation-equal whole
  // lines ("Fever with chills" / "fever with chills.") rendered as two
  // TYPED rows. Fold the store rows here, ONE fold rule shared with every
  // other field, before deriving either shape. Still memoised on the
  // ORIGINAL `rows` reference (the WeakMap key), so a re-render that didn't
  // change `memory` never re-derives.
  const foldedRows = foldDuplicateRows(rows);

  const typedRows = [];
  const individualByKey = new Map();
  // r5 — merges two candidates' `fromLine` (the whole store line that fed
  // this contribution, plus its own `c`/`t` for the final rank sort) by
  // line-identity, so the same comma line is never listed twice even if it
  // (defensively) split into a duplicate clause.
  const mergeFromLines = (a, b) => {
    const byKey = new Map();
    for (const item of [...(a || []), ...(b || [])]) byKey.set(normaliseRowKey(item.v), item);
    return [...byKey.values()];
  };
  const mergeIndividual = (key, candidate) => {
    const existing = individualByKey.get(key);
    if (!existing) { individualByKey.set(key, candidate); return; }
    const displayV = candidate.c > existing.c ? candidate.v : existing.v;
    individualByKey.set(key, {
      v: displayV,
      c: existing.c + candidate.c,
      t: Math.max(existing.t, candidate.t),
      src: existing.src === 'store' || candidate.src === 'store' ? 'store' : 'derived',
      fromLines: mergeFromLines(existing.fromLines, candidate.fromLines),
    });
  };

  for (const row of foldedRows) {
    // bk-1287 — comma-BEARING means "splits into MORE THAN ONE clause", not
    // "the raw string contains a comma character" (a trailing-comma-only
    // line has one clause after the empty tail is dropped).
    const clauses = splitMemoryClauses(row.v);
    if (clauses.length <= 1) {
      // Owner ruling — a comma-free (or trailing-comma-only) line is a
      // single value: individual only, no typed twin. Display text is the
      // clause itself (trimmed, comma stripped) when one survived the
      // split, else the raw line (guards the CO_CLAUSE_MAX_LEN edge: an
      // over-long comma-free line still offers its raw text, unfiltered,
      // exactly as before this change).
      const displayV = clauses[0] || row.v;
      const key = normaliseRowKey(displayV);
      if (!key) continue;
      mergeIndividual(key, { v: displayV, c: row.c, t: row.t, src: 'store', fromLines: [] });
      continue;
    }
    typedRows.push({ v: row.v, c: row.c, t: row.t, shape: 'typed', src: 'store' });
    for (const clause of clauses) {
      const key = normaliseRowKey(clause);
      if (!key) continue;
      mergeIndividual(key, {
        v: clause, c: row.c, t: row.t, src: 'derived',
        fromLines: [{ v: row.v, c: row.c, t: row.t }],
      });
    }
  }
  const individualRows = [...individualByKey.values()].map(({ fromLines, ...row }) => ({
    ...row,
    shape: 'individual',
    from: [...fromLines].sort((a, b) => (b.c - a.c) || (b.t - a.t)).map((line) => line.v),
  }));

  const expanded = [...individualRows, ...typedRows];
  _expandCoRowsCache.set(rows, expanded);
  return expanded;
}

/** Which of the 6 typing bands (architect-design.md §3) a row falls into
 * for a normalised, non-empty `q`; `-1` when it does not match at all
 * (excluded, not ranked last — §3's "non-matching rows are excluded"). */
function coBandFor(row, q) {
  const v = normaliseMemoryValue(row.v);
  let tier;
  if (v === q) tier = 0;
  else if (v.startsWith(q)) tier = 1;
  else if (v.includes(q)) tier = 2;
  else return -1;
  return row.shape === 'individual' ? tier : tier + 3;
}

/**
 * coMemoryRows — THE one place C/O ranking/derivation happens (architect-
 * design.md §3's canonical spec); every C/O surface renders this output,
 * never re-derives it. Query non-empty: 6 bands (individual exact ·
 * starts-with · contains · typed exact · starts-with · contains),
 * non-matching rows excluded. Query empty: 2 groups (individuals, then
 * typed), no bands. Within any band/group: `c` desc → `t` desc →
 * first-appearance (a stable sort over `expandCoRows`'s own insertion
 * order carries this for free — never a second comparator).
 *
 * Does NOT reuse `filterMemoryRows` — that is a plain substring gate with
 * no band ordering, so it cannot implement §3's rule.
 *
 * @param {Object} memory  the mounted G1 `memory` object (or undefined)
 * @param {string} query   the active segment the doctor has typed (from
 *   the pad's own `getActiveSegment` — a DIFFERENT split rule, see
 *   `splitMemoryClauses`'s own note)
 * @param {{ alreadySelectedText?: string }} opts  `alreadySelectedText` —
 *   the field's current raw value; a row already contained in it is
 *   dropped, same rule `memoryFieldRows`'s (c) uses.
 * @returns {{ rows: Array, total: number, matched: number }}
 */
export function coMemoryRows(memory, query = '', { alreadySelectedText = '' } = {}) {
  const rows = memory?.types?.[MEMORY_TYPES.CO]?.rows || [];
  const expanded = expandCoRows(rows);
  const selectedKey = normaliseRowKey(alreadySelectedText);
  const notAlreadySelected = selectedKey
    ? expanded.filter((row) => !selectedKey.includes(normaliseRowKey(row.v)))
    : expanded;

  const q = normaliseMemoryValue(query);
  const banded = q
    ? notAlreadySelected.map((row) => ({ row, band: coBandFor(row, q) })).filter(({ band }) => band >= 0)
    : notAlreadySelected.map((row) => ({ row, band: row.shape === 'individual' ? 0 : 1 }));

  const ranked = banded
    .sort((a, b) => (a.band - b.band) || (b.row.c - a.row.c) || (b.row.t - a.row.t))
    .map(({ row }) => row);

  return { rows: ranked, total: notAlreadySelected.length, matched: ranked.length };
}

/**
 * recentCoValues — RX-FORM-BUGS item 3, the ONE ranking every "recent" C/O
 * pill reads (owner-flagged assumption, `brief.md` "Assumption flagged to
 * the owner"): individual (comma-split) values only — a long typed line
 * makes a bad pill, so whole lines stay dropdown-only — newest `t` first,
 * `c` (usage) the tiebreak. Reuses `expandCoRows`, the SAME individual/typed
 * split the C/O dropdown already renders, so a pill and its dropdown twin
 * can never disagree about what "individual" means. New array; the input
 * `memory` is never mutated.
 *
 * @param {Object} memory  the mounted G1 `memory` object (or undefined)
 * @param {number} limit
 * @returns {Array<{v: string, c: number, t: number, shape: 'individual', src: string}>}
 */
export function recentCoValues(memory, limit = 5) {
  const rows = memory?.types?.[MEMORY_TYPES.CO]?.rows || [];
  const individual = expandCoRows(rows).filter((row) => row.shape === 'individual');
  return [...individual].sort((a, b) => (b.t - a.t) || (b.c - a.c)).slice(0, limit);
}

/**
 * mergeMemoryDelta — the optimistic merge of a G2 `memory_delta` into the
 * mounted `memory` object, so a value saved this visit shows up in its
 * group with no refetch (S2 AC8). Pure: returns a new object, never
 * mutates `memory`. `novel` ⇒ insert at `c: 1`; else find the existing row
 * (normalised match) and `c += 1`, `t = now`; re-rank.
 *
 * @param {Object} memory  the mounted G1 `memory` object
 * @param {{ owner_member_id: string, items: Array<{type: string, v: string, novel: boolean}> } | null} delta
 * @returns {Object} a new `memory` object (the same object when `delta` is null/empty)
 */
export function mergeMemoryDelta(memory, delta) {
  if (!delta || !Array.isArray(delta.items) || delta.items.length === 0) return memory;
  const now = Math.floor(Date.now() / 1000);
  const types = { ...(memory?.types || {}) };
  for (const item of delta.items) {
    const existingRows = types[item.type]?.rows || [];
    let rows;
    if (item.novel) {
      rows = [...existingRows, { v: item.v, c: 1, t: now }];
    } else {
      const key = normaliseMemoryValue(item.v);
      let found = false;
      rows = existingRows.map((row) => {
        if (!found && normaliseMemoryValue(row.v) === key) {
          found = true;
          return { ...row, c: row.c + 1, t: now };
        }
        return row;
      });
      if (!found) rows = [...rows, { v: item.v, c: 1, t: now }];
    }
    types[item.type] = { ...types[item.type], rows: rankRows(rows) };
  }
  return { ...memory, types };
}

/**
 * removeMemoryRow — FE-12 forget (G5): strips one row, matched by the SAME
 * row-identity (`normaliseRowKey`) every dropdown's own dedupe already uses,
 * from a field's memory list. Pure; returns a NEW `memory` object, never
 * mutates. A no-op (same reference) when nothing matched — mirrors
 * `mergeMemoryDelta`'s own no-op rule, so a caller can always call this and
 * check reference equality to know whether anything changed.
 *
 * @param {Object} memory  the mounted G1 `memory` object
 * @param {string} type    MEMORY_TYPES.*
 * @param {string} value   the row's own `v` (the saved value, verbatim)
 */
export function removeMemoryRow(memory, type, value) {
  const rows = memory?.types?.[type]?.rows || [];
  const key = normaliseRowKey(value);
  const filtered = rows.filter((row) => normaliseRowKey(row.v) !== key);
  if (filtered.length === rows.length) return memory;
  return { ...memory, types: { ...memory.types, [type]: { ...memory.types[type], rows: filtered } } };
}

/**
 * restoreMemoryRow — FE-12 Undo. Forget has NO server-side undo (the
 * frozen G5 contract's own rule — "forget ≠ blocklist": a re-typed value
 * simply re-learns at count 1 on the next save). Undo is a pure CLIENT-side
 * re-insert of the EXACT row object this session just removed, ranked back
 * into place the same way a save's `memory_delta` merge already ranks a
 * restored row — never a second network call.
 *
 * @param {Object} memory  the mounted G1 `memory` object
 * @param {string} type    MEMORY_TYPES.*
 * @param {Object} row     the exact row object `removeMemoryRow` took out
 */
export function restoreMemoryRow(memory, type, row) {
  const rows = memory?.types?.[type]?.rows || [];
  const key = normaliseRowKey(row.v);
  if (rows.some((r) => normaliseRowKey(r.v) === key)) return memory; // already there
  return { ...memory, types: { ...memory.types, [type]: { ...memory.types[type], rows: rankRows([...rows, row]) } } };
}
